import {
  Building2Icon,
  ContactRoundIcon,
  KeyRoundIcon,
  LogOutIcon,
  MegaphoneIcon,
  TagsIcon,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SidebarTrigger } from "@/components/ui/Sidebar";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/Sidebar";
import { Link, useLocation } from "react-router";
import { useAuthContext } from "@/contexts/AuthProvider";

const navigationItems = [
  {
    title: "Campaigns",
    url: "/",
    icon: MegaphoneIcon,
  },
  {
    title: "KOL Directory",
    url: "/kols",
    icon: ContactRoundIcon,
  },
  {
    title: "Brand",
    url: "/brands",
    icon: Building2Icon,
  },
  {
    title: "Topics",
    url: "/topics",
    icon: TagsIcon,
  },
];

const settingItems = [
  {
    title: "API Access",
    url: "/api-key",
    icon: KeyRoundIcon,
  },
];

function isItemActive(pathname: string, url: string) {
  if (url === "/") {
    return pathname === "/" || pathname.startsWith("/campaigns");
  }
  return pathname === url || pathname.startsWith(`${url}/`);
}

function initials(name?: string) {
  if (!name) return "GU";
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "GU"
  );
}

function Layout({ children }: { children?: React.ReactNode }) {
  const { pathname } = useLocation();
  const { user, isLoading, logout } = useAuthContext();
  const isGuestKolPage = !user && pathname.startsWith("/kols");

  return (
    <SidebarProvider>
      <Sidebar className="border-r border-line">
        <SidebarHeader className="px-5 pt-6 pb-7">
          <Link to="/" className="flex flex-col items-center gap-1.5">
            <img
              src="/bion-wordmark.png"
              alt="Bion"
              className="h-10 w-auto object-contain"
            />
            <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Intelligence
            </span>
          </Link>
        </SidebarHeader>

        <SidebarContent className="px-3">
          <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.11em] text-[#969b93]">
            Workspace
          </div>
          <SidebarMenu className="gap-0.5">
            {navigationItems.map((item) => (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton
                  isActive={isItemActive(pathname, item.url)}
                  asChild
                  className="h-11 rounded-xl px-3 text-[12px] font-medium text-[#5f665e] hover:bg-white/80 hover:text-ink data-[active=true]:bg-cream data-[active=true]:font-semibold data-[active=true]:text-ink data-[active=true]:[&>svg]:text-coral-deep"
                >
                  <Link to={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>

          <div className="mt-6 px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.11em] text-[#969b93]">
            Settings
          </div>
          <SidebarMenu className="gap-0.5">
            {settingItems.map((item) => (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton
                  isActive={isItemActive(pathname, item.url)}
                  asChild
                  className="h-11 rounded-xl px-3 text-[12px] font-medium text-[#5f665e] hover:bg-white/80 hover:text-ink data-[active=true]:bg-cream data-[active=true]:font-semibold data-[active=true]:text-ink data-[active=true]:[&>svg]:text-coral-deep"
                >
                  <Link to={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter className="p-3 pb-5">
          {isGuestKolPage ? (
            <Button asChild className="w-full">
              <Link
                to="https://wa.me/628119721120"
                target="_blank"
                rel="noopener"
              >
                Contact Us
              </Link>
            </Button>
          ) : (
            <div className="flex items-center gap-2.5 rounded-2xl border border-line bg-white/60 px-3 py-3">
              <div className="grid size-9 flex-none place-items-center rounded-full bg-cream text-[11px] font-bold text-olive-deep">
                {initials(user?.name)}
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <strong className="truncate text-[12px] font-semibold text-ink">
                  {user?.name ?? "Guest"}
                </strong>
                <span className="truncate text-[10px] capitalize text-muted-foreground">
                  {user?.role ?? "Visitor"}
                </span>
              </div>
              <button
                type="button"
                onClick={logout}
                aria-label="Logout"
                className="grid size-8 flex-none cursor-pointer place-items-center rounded-[9px] text-muted-foreground transition-colors hover:bg-cream hover:text-coral-deep"
              >
                <LogOutIcon className="size-4" />
              </button>
            </div>
          )}
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="overflow-hidden">
        <header className="sticky top-0 z-30 flex h-[74px] shrink-0 items-center gap-3 border-b border-line bg-background/90 px-4 backdrop-blur-md lg:px-8">
          <SidebarTrigger className="-ml-1 md:hidden" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {isGuestKolPage ? "Welcome" : "Welcome,"}
            </span>
            <strong className="truncate font-display text-sm leading-none font-semibold">
              {isGuestKolPage
                ? "Guest"
                : isLoading
                  ? "Loading..."
                  : (user?.name ?? "Guest")}
            </strong>
          </div>
        </header>

        <div className="relative flex flex-1 flex-col">
          <div className="mx-auto w-full max-w-[1500px] px-4 py-7 pb-12 lg:px-8 lg:pb-14">
            {children}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

export { Layout };
